import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, Check, X, Building2, Phone, ChevronRight, Loader2, Calendar, AlertCircle, Send, CalendarPlus, Download, MessageCircle, Mail, Bell, BellOff, PhoneCall } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { APPOINTMENT_STATUS_CONFIG, APPOINTMENT_REJECT_REASONS } from '../constants/marketplace';
import { formatAppointmentDateLong, isPastAppointment } from '../utils/formatters';
import { downloadICS, getGoogleCalendarUrl, getAppointmentWhatsAppUrl, getAppointmentEmailUrl } from '../utils/calendar';

const STATUS_CONFIG = APPOINTMENT_STATUS_CONFIG;
const REJECT_REASONS = APPOINTMENT_REJECT_REASONS;

const RejectModal = ({ appointment, onClose, onReject }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [suggestedDate, setSuggestedDate] = useState('');
  const [suggestedTime, setSuggestedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const otherParty = appointment.buyer;

  const handleSubmit = async () => {
    setLoading(true);
    const finalReason = reason === 'other' ? customReason : reason;
    await onReject(appointment.id, {
      reason: finalReason || null,
      suggested_date: suggestedDate || null,
      suggested_time: suggestedTime || null,
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-dark-card w-full sm:w-[440px] sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-dark-border">
          <h2 className="text-lg font-bold text-primary">Refuser le rendez-vous</h2>
          <button onClick={onClose} className="p-1 hover:bg-dark-hover rounded-full transition-colors">
            <X className="h-5 w-5 text-muted" />
          </button>
        </div>

        {/* Appointment summary */}
        <div className="px-5 py-3 bg-dark-hover text-sm text-secondary">
          <span className="font-medium text-primary">{appointment.vehicle?.make} {appointment.vehicle?.model}</span>
          {' — '}
          {formatAppointmentDateLong(appointment.date)} à {appointment.time}
        </div>

        <div className="p-5 space-y-4">
          {/* Reason picker */}
          <div>
            <label className="block text-sm font-medium text-label mb-2">Raison du refus</label>
            <div className="space-y-2">
              {REJECT_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                    reason === r.value
                      ? 'border-red-400 bg-red-500/10'
                      : 'border-dark-border hover:border-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-label">{r.label}</span>
                </label>
              ))}
            </div>
            {reason === 'other' && (
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Précisez la raison..."
                className="w-full mt-2 px-3 py-2.5 border-2 border-dark-border bg-dark-input rounded-lg text-sm text-primary placeholder-muted focus:border-red-400 focus:ring-0 transition-colors"
                maxLength={200}
                autoFocus
              />
            )}
          </div>

          {/* Alternative proposal */}
          <div className="bg-carmin-50 rounded-lg p-4">
            <p className="text-sm font-medium text-carmin-light mb-3">
              Proposer un créneau alternatif <span className="text-carmin/60 font-normal">(optionnel)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-carmin mb-1">Date</label>
                <input
                  type="date"
                  value={suggestedDate}
                  onChange={(e) => setSuggestedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm text-primary focus:border-carmin focus:ring-0 bg-dark-input"
                />
              </div>
              <div>
                <label className="block text-xs text-carmin mb-1">Heure</label>
                <input
                  type="time"
                  value={suggestedTime}
                  onChange={(e) => setSuggestedTime(e.target.value)}
                  step="1800"
                  className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm text-primary focus:border-carmin focus:ring-0 bg-dark-input"
                />
              </div>
            </div>
          </div>

          {/* Call directly */}
          {otherParty?.phone && (
            <a
              href={`tel:${otherParty.phone}`}
              className="flex items-center justify-center gap-2 w-full py-3 bg-carmin-50 text-carmin rounded-xl text-sm font-medium hover:bg-carmin/20 transition-colors border border-carmin-dark/30"
            >
              <Phone className="h-4 w-4" />
              Appeler {otherParty.display_name || 'l\'acheteur'} ({otherParty.phone})
            </a>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-dark-border text-label rounded-xl text-sm font-medium hover:bg-dark-hover transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Refuser
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AppointmentCard = ({ appointment, onAction, onReject, onToggleReminder }) => {
  const vehicle = appointment.vehicle || {};
  const otherParty = appointment.is_seller ? appointment.buyer : appointment.seller;
  const status = STATUS_CONFIG[appointment.status] || STATUS_CONFIG.pending;
  const [acting, setActing] = useState(null);
  const isPast = isPastAppointment(appointment.date);
  const isInactive = isPast || ['rejected', 'cancelled'].includes(appointment.status);

  const handleAction = async (action) => {
    setActing(action);
    await onAction(appointment.id, action);
    setActing(null);
  };

  return (
    <div className={`bg-dark-card rounded-xl border border-dark-border overflow-hidden transition-all ${
      isInactive ? 'opacity-50 grayscale' : ''
    }`}>
      <div className="flex">
        {/* Vehicle thumbnail */}
        <div className="w-24 h-24 flex-shrink-0 bg-dark-hover">
          {vehicle.images?.[0] ? (
            <img src={vehicle.images[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted">
              <Calendar className="h-8 w-8" />
            </div>
          )}
        </div>

        <div className="flex-1 p-3 min-w-0">
          {/* Vehicle + status */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link
                to={`/marketplace/${appointment.listing_id}`}
                className="text-sm font-semibold text-primary hover:text-carmin truncate block"
              >
                {vehicle.make} {vehicle.model} {vehicle.year}
              </Link>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Building2 className="h-3 w-3 text-muted" />
                <span className="text-xs text-muted truncate">{otherParty?.display_name}</span>
              </div>
            </div>
            <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
              {status.label}
            </span>
          </div>

          {/* Date + time */}
          <div className="flex items-center gap-3 mt-2">
            <span className="inline-flex items-center gap-1 text-xs text-secondary">
              <CalendarDays className="h-3.5 w-3.5" />
              {formatAppointmentDateLong(appointment.date)}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-secondary">
              <Clock className="h-3.5 w-3.5" />
              {appointment.time}
            </span>
          </div>

          {/* Message */}
          {appointment.message && (
            <p className="text-xs text-muted mt-1.5 truncate">{appointment.message}</p>
          )}

          {/* Rejection info (shown to buyer) */}
          {appointment.status === 'rejected' && (appointment.reject_reason || appointment.suggested_date) && (
            <div className="mt-2 p-2 bg-red-500/10 rounded-lg space-y-1">
              {appointment.reject_reason && (
                <div className="flex items-start gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-400">{appointment.reject_reason}</p>
                </div>
              )}
              {appointment.suggested_date && (
                <div className="flex items-start gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-carmin mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-carmin">
                    Créneau proposé : {formatAppointmentDateLong(appointment.suggested_date)}
                    {appointment.suggested_time && ` à ${appointment.suggested_time}`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Contact info when confirmed */}
          {appointment.status === 'confirmed' && otherParty?.phone && (
            <a
              href={`tel:${otherParty.phone}`}
              className="inline-flex items-center gap-1 text-xs text-carmin hover:text-carmin-light mt-1.5"
            >
              <Phone className="h-3 w-3" />
              {otherParty.phone}
            </a>
          )}

          {/* Actions */}
          {appointment.status === 'pending' && (
            <div className="flex gap-2 mt-2">
              {appointment.is_seller && (
                <>
                  <button
                    onClick={() => handleAction('confirm')}
                    disabled={acting}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-carmin text-white rounded-lg text-xs font-medium hover:bg-carmin-light disabled:opacity-50"
                  >
                    {acting === 'confirm' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                    Confirmer
                  </button>
                  <button
                    onClick={() => onReject(appointment)}
                    disabled={acting}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-500/30 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/10 disabled:opacity-50"
                  >
                    <X className="h-3 w-3" />
                    Refuser
                  </button>
                </>
              )}
              <button
                onClick={() => handleAction('cancel')}
                disabled={acting}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-dark-border text-secondary rounded-lg text-xs font-medium hover:bg-dark-hover disabled:opacity-50"
              >
                {acting === 'cancel' ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                Annuler
              </button>
            </div>
          )}

          {appointment.status === 'confirmed' && (
            <>
              {/* Calendar & sharing actions */}
              {!isInactive && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <a
                    href={getGoogleCalendarUrl(appointment, vehicle)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-dark-hover text-label rounded-md text-[11px] font-medium hover:bg-dark-surface transition-colors"
                  >
                    <CalendarPlus className="h-3 w-3" />
                    Agenda
                  </a>
                  <button
                    onClick={() => downloadICS(appointment, vehicle)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-dark-hover text-label rounded-md text-[11px] font-medium hover:bg-dark-surface transition-colors"
                  >
                    <Download className="h-3 w-3" />
                    .ics
                  </button>
                  <a
                    href={getAppointmentWhatsAppUrl(appointment, vehicle, otherParty?.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 rounded-md text-[11px] font-medium hover:bg-green-500/20 transition-colors"
                  >
                    <MessageCircle className="h-3 w-3" />
                    WhatsApp
                  </a>
                  <a
                    href={getAppointmentEmailUrl(appointment, vehicle, null)}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-dark-hover text-label rounded-md text-[11px] font-medium hover:bg-dark-surface transition-colors"
                  >
                    <Mail className="h-3 w-3" />
                    Email
                  </a>
                </div>
              )}

              {/* Reminder toggles + cancel */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {!isInactive && (
                  <>
                    <button
                      onClick={() => onToggleReminder(appointment.id, 'sms', !appointment.sms_reminder_requested)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
                        appointment.sms_reminder_requested
                          ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
                          : 'bg-dark-hover text-muted border border-dark-border hover:bg-dark-surface'
                      }`}
                      title={appointment.sms_reminder_requested ? 'Rappel SMS activé' : 'Activer le rappel SMS'}
                    >
                      {appointment.sms_reminder_requested ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                      SMS
                    </button>
                    <button
                      onClick={() => onToggleReminder(appointment.id, 'call', !appointment.call_reminder_requested)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
                        appointment.call_reminder_requested
                          ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20'
                          : 'bg-dark-hover text-muted border border-dark-border hover:bg-dark-surface'
                      }`}
                      title={appointment.call_reminder_requested ? 'Rappel appel activé' : 'Activer le rappel appel'}
                    >
                      <PhoneCall className="h-3 w-3" />
                      Appel
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleAction('cancel')}
                  disabled={acting}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-dark-border text-secondary rounded-md text-[11px] font-medium hover:bg-dark-hover disabled:opacity-50"
                >
                  {acting === 'cancel' ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  Annuler le RDV
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const MyAppointments = () => {
  const [tab, setTab] = useState('received');
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rejectingAppointment, setRejectingAppointment] = useState(null);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/marketplace/appointments/${tab}`);
      setAppointments(response.data.appointments || []);
    } catch (err) {
      setError('Erreur lors du chargement des rendez-vous');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleAction = async (appointmentId, action) => {
    try {
      await api.post(`/marketplace/appointments/${appointmentId}/${action}`);
      loadAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (appointmentId, rejectData) => {
    try {
      await api.post(`/marketplace/appointments/${appointmentId}/reject`, rejectData);
      setRejectingAppointment(null);
      loadAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  const handleToggleReminder = async (appointmentId, type, enabled) => {
    try {
      const endpoint = type === 'call' ? 'call-reminder' : 'sms-reminder';
      await api.post(`/marketplace/appointments/${appointmentId}/${endpoint}`, { enabled });
      loadAppointments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const pendingReceived = appointments.filter(a => a.status === 'pending' && a.is_seller).length;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-carmin" />
            Mes rendez-vous
          </h1>
          <p className="text-sm text-muted mt-1">Gérez vos visites de véhicules</p>
        </div>
        <Link
          to="/marketplace"
          className="inline-flex items-center text-sm text-carmin hover:text-carmin-light"
        >
          Marketplace <ChevronRight className="h-4 w-4 ml-0.5" />
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-hover rounded-lg p-1 mb-6">
        <button
          onClick={() => setTab('received')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            tab === 'received' ? 'bg-theme-inverted text-theme-inverted' : 'text-secondary hover:text-primary'
          }`}
        >
          Reçus
          {pendingReceived > 0 && tab === 'received' && (
            <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5">{pendingReceived}</span>
          )}
        </button>
        <button
          onClick={() => setTab('sent')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            tab === 'sent' ? 'bg-theme-inverted text-theme-inverted' : 'text-secondary hover:text-primary'
          }`}
        >
          Envoyés
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-400 p-3 rounded-lg mb-4 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-12"><LoadingSpinner size="lg" /></div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-16">
          <CalendarDays className="h-16 w-16 text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            {tab === 'received' ? 'Aucune demande de visite' : 'Aucun rendez-vous envoyé'}
          </h3>
          <p className="text-muted text-sm">
            {tab === 'received'
              ? 'Les demandes de visite de vos annonces apparaîtront ici.'
              : 'Demandez une visite depuis une annonce marketplace.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map(appointment => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              onAction={handleAction}
              onReject={(appt) => setRejectingAppointment(appt)}
              onToggleReminder={handleToggleReminder}
            />
          ))}
        </div>
      )}

      {rejectingAppointment && (
        <RejectModal
          appointment={rejectingAppointment}
          onClose={() => setRejectingAppointment(null)}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

export default MyAppointments;
